import { useEffect, useRef } from "react";
import {
  BookOpen,
  BriefcaseBusiness,
  CalendarDays,
  ClipboardList,
  Compass,
  LayoutGrid,
  LogOut,
  MessageCircle,
  Settings,
  Sparkles,
  UserCog,
  X,
} from "lucide-react";
import { NavLink } from "react-router-dom";
import axios from "axios";

import { useAppSelector } from "../../../hooks/reducer";

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

type NavigationItem = {
  label: string;
  description: string;
  to: string;
  icon: typeof LayoutGrid;
};

const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps) => {
  const sidebar = useRef<HTMLDivElement>(null);
  const user = useAppSelector((state) => state.authReducer.data?.user);
  const isSuperAdmin = user?.role === "super_admin";
  const isCareerManager = user?.role === "career_manager";
  const isLegacyAdmin = Boolean(
    user?.isAdmin && (!user?.role || user.role === "admin")
  );
  const isEducationAdmin =
    user?.role === "education_admin" || user?.role === "admin" || isLegacyAdmin;
  const isAdmin = Boolean(isSuperAdmin || isCareerManager || isEducationAdmin);
  const isCandidate = Boolean(
    user && !isAdmin && user.programAccess !== "active"
  );

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSidebarOpen(false);
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [setSidebarOpen]);

  const navigation: NavigationItem[] = isCandidate
    ? [
        {
          label: "Моя заявка",
          description: "Статус розгляду",
          to: "/application-status",
          icon: ClipboardList,
        },
        {
          label: "Профіль",
          description: "Дані та безпека",
          to: "/settings",
          icon: Settings,
        },
      ]
    : [
    ...(isAdmin
      ? [
          ...(isSuperAdmin
            ? [
                {
                  label: "Користувачі",
                  description: "Ролі та доступи",
                  to: "/admin/users",
                  icon: UserCog,
                },
              ]
            : []),
          ...(isSuperAdmin || isCareerManager
            ? [
                {
                  label: "Робота",
                  description: "Вакансії та консультації",
                  to: "/admin/career",
                  icon: BriefcaseBusiness,
                },
              ]
            : []),
          ...(isSuperAdmin
            ? [
          {
            label: "Заявки",
            description: "Відбір кандидатів",
            to: "/admin/applications",
            icon: ClipboardList,
          },
              ]
            : []),
          ...(isSuperAdmin || isEducationAdmin
            ? [
          {
            label: "Календар потоків",
            description: "Створення активностей",
            to: "/admin/calendar",
            icon: CalendarDays,
          },
              ]
            : []),
          {
            label: "Чати потоків",
            description: "Спілкування з учасниками",
            to: "/admin/chat",
            icon: MessageCircle,
          },
        ]
      : [
          {
            label: "Календар",
            description: "Активності мого потоку",
            to: "/calendar",
            icon: CalendarDays,
          },
          {
            label: "Чат потоку",
            description: "Спілкування й обговорення",
            to: "/chat",
            icon: MessageCircle,
          },
          {
            label: "Мої курси",
            description: "Навчання та прогрес",
            to: "/my-courselist",
            icon: BookOpen,
          },
        ]),
    ...(!isAdmin
      ? [
          {
            label: "Мій маршрут",
            description: "Roadmap і прогрес",
            to: "/cabinet/yellow-quest",
            icon: Sparkles,
          },
        ]
      : []),
    ...(isAdmin
      ? [
          ...(isSuperAdmin || isEducationAdmin
            ? [
          {
            label: "Мої курси",
            description: "Курси, учасники та перевірка",
            to: "/admin/self-courses",
            icon: BookOpen,
          },
              ]
            : []),
        ]
      : []),
    {
      label: "Профіль",
      description: "Дані та безпека",
      to: "/settings",
      icon: Settings,
    },
  ];

  const displayName =
    user?.username ||
    user?.email?.split("@")[0] ||
    (isAdmin ? "Адміністратор" : isCandidate ? "Кандидат" : "Учасник");
  const roleLabel = isSuperAdmin
    ? "Супер-адмін"
    : isCareerManager
    ? "Кар'єрний менеджер"
    : isEducationAdmin
    ? "Освітній адмін"
    : isCandidate
    ? "Кандидат"
    : "Учасник";
  const initials = displayName
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleLogout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common.Authorization;
    window.location.reload();
  };

  return (
    <>
      <button
        type="button"
        aria-label="Закрити меню"
        className={`fixed inset-0 z-40 bg-black/35 transition-opacity lg:hidden ${
          sidebarOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={() => setSidebarOpen(false)}
      />

      <aside
        ref={sidebar}
        className={`fixed inset-y-0 left-0 z-50 flex h-[100dvh] w-[min(300px,calc(100vw-32px))] flex-col border-r-2 border-black bg-white shadow-[10px_0_35px_rgba(15,23,42,0.08)] transition-transform duration-300 lg:static lg:h-auto lg:w-[292px] lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-24 shrink-0 items-center justify-between gap-2 border-b-2 border-black bg-[#fffdf2] px-3 sm:px-4">
          <NavLink
            to={
              isSuperAdmin
                ? "/admin/users"
                : isCareerManager
                ? "/admin/career"
                : isEducationAdmin
                ? "/admin/calendar"
                : isCandidate
                ? "/application-status"
                : "/calendar"
            }
            className="flex min-w-0 items-center gap-2 sm:gap-3"
            onClick={() => setSidebarOpen(false)}
          >
            <img
              className="h-auto w-[112px] shrink-0"
              src="/assets/Images/yellow_logo_pl.png"
              alt="Yellow Hub"
            />
            <div className="min-w-0">
              <p className="rounded-full border border-black/10 bg-white px-2 py-1 text-[10px] font-black uppercase tracking-[0.08em] text-black/65">
                Кабінет
              </p>
            </div>
          </NavLink>

          <button
            type="button"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border-2 border-black bg-[#ffd400] text-black shadow-[3px_3px_0_#000] lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Закрити меню"
          >
            <X size={20} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3 py-5">
          <p className="px-2 text-[11px] font-black uppercase tracking-[0.16em] text-[#8a6f00]">
            Робочий простір
          </p>

          <nav className="mt-4 space-y-3 border-t border-black/10 pt-4">
            {navigation.map(({ label, description, to, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `group relative z-0 flex min-h-[76px] w-full items-center gap-3 overflow-hidden rounded-[18px] border-2 px-3 py-3 transition ${
                    isActive
                      ? "border-black bg-[#ffd400] text-black shadow-none lg:shadow-[3px_3px_0_#000]"
                      : "border-transparent text-[#3f3f3f] hover:border-black/10 hover:bg-[#fff8cf]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <div
                      className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl border ${
                        isActive
                          ? "border-black bg-black text-[#ffd400]"
                          : "border-black/10 bg-white text-[#4b5563] group-hover:text-black"
                      }`}
                    >
                      <Icon size={18} />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col justify-center">
                      <p className="truncate text-[14px] font-black leading-[1.1] text-inherit">{label}</p>
                      <p
                        className={`mt-1 truncate text-[12px] leading-[1.15] ${
                          isActive ? "text-black/65" : "text-[#8a8a8a]"
                        }`}
                      >
                        {description}
                      </p>
                    </div>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="shrink-0 border-t-2 border-black bg-[#fffdf2] p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          <div className="mb-2 flex items-center gap-3 rounded-2xl border border-black/10 bg-white p-3">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border-2 border-black bg-[#ffd400] text-sm font-black text-black shadow-[3px_3px_0_#000]">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-black text-black">{displayName}</p>
              <p className="text-xs font-semibold text-[#7a7a7a]">
                {roleLabel}
              </p>
            </div>
          </div>

          <NavLink
            to="/start"
            onClick={() => setSidebarOpen(false)}
            className="mb-2 flex w-full items-center gap-3 rounded-2xl border border-black/10 bg-white px-3 py-2.5 text-sm font-black text-[#4b5563] transition hover:bg-[#fff8cf] hover:text-black"
          >
            <Compass size={18} />
            На головну сайту
          </NavLink>

          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-black text-[#8b1e1e] transition hover:bg-red-50"
          >
            <LogOut size={18} />
            Вийти з кабінету
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
